import React from 'react';
import {
  PiArrowSquareOutLight,
  PiGlobeHemisphereWestLight,
  PiGlobeXLight,
  PiPencilSimpleLineLight,
} from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { QuickSelectionMenu } from 'src/modules/shared/components/molecules/dropdowns/quick-selection-menu';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type {
  ComponentAppearance,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
import type {
  PropertyPublicationActionItem,
  PropertyPublicationActionsProps,
} from '../../types/property-publication-actions.type';

const iconByKind: Record<
  PropertyPublicationActionItem['kind'],
  React.ReactNode
> = {
  manage: <PiPencilSimpleLineLight className="h-4 w-4" />,
  open_external: <PiArrowSquareOutLight className="h-4 w-4" />,
  publish: <PiGlobeHemisphereWestLight className="h-4 w-4" />,
  unpublish: <PiGlobeXLight className="h-4 w-4" />,
};

const defaultVariantByKind: Record<
  PropertyPublicationActionItem['kind'],
  ComponentVariant
> = {
  manage: 'neutral',
  open_external: 'neutral',
  publish: 'primary',
  unpublish: 'danger',
};

const defaultAppearanceByKind: Record<
  PropertyPublicationActionItem['kind'],
  ComponentAppearance
> = {
  manage: 'ghost',
  open_external: 'ghost',
  publish: 'soft',
  unpublish: 'soft',
};

export const PropertyPublicationActions: React.FC<
  PropertyPublicationActionsProps
> = ({ actions }) => {
  const { t } = useI18n();
  const externalActions = actions.filter(
    (action) => action.kind === 'open_external'
  );
  const menuAction =
    externalActions.length > 1 ? externalActions[0] : undefined;

  if (actions.length === 0) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-1 sm:gap-2">
      {actions.map((action) => {
        if (menuAction && action.kind === 'open_external') {
          return action === menuAction ? (
            <QuickSelectionMenu
              key="external_publications"
              label={t(
                'catalog.properties.editor.publishing.actions.openExternal'
              )}
              caption={t(
                'catalog.properties.editor.publishing.actions.openInMenuCaption'
              )}
              icon={iconByKind.open_external}
              size="sm"
              items={externalActions.map((externalAction) => ({
                key: externalAction.key,
                label: externalAction.displayLabel ?? externalAction.label,
                ariaLabel: externalAction.label,
                icon: iconByKind.open_external,
                disabled: externalAction.disabled,
                onClick: externalAction.onClick,
              }))}
            />
          ) : null;
        }

        return (
          <Tooltip
            key={action.key}
            content={
              action.tooltipTitle ||
              action.tooltipBody ||
              action.tooltipFooter ? (
                <div className="flex max-w-sm flex-col gap-2 text-left">
                  {action.tooltipTitle ? (
                    <Text size="small" weight="semibold" className="text-white">
                      {action.tooltipTitle}
                    </Text>
                  ) : null}
                  {action.tooltipBody?.length ? (
                    <div className="flex flex-col gap-1">
                      {action.tooltipBody.map((line, index) => (
                        <Text key={index} size="small" className="text-white">
                          {line}
                        </Text>
                      ))}
                    </div>
                  ) : null}
                  {action.tooltipFooter ? (
                    <Text size="small" className="text-slate-200">
                      {action.tooltipFooter}
                    </Text>
                  ) : null}
                </div>
              ) : (
                action.tooltip ?? action.label
              )
            }
            size="xs"
            className={action.tooltipClassName}
          >
            <Button
              type="button"
              variant={action.variant ?? defaultVariantByKind[action.kind]}
              appearance={
                action.appearance ?? defaultAppearanceByKind[action.kind]
              }
              size="sm"
              isIconOnly
              aria-label={action.label}
              disabled={action.disabled}
              onClick={action.onClick}
            >
              {iconByKind[action.kind]}
            </Button>
          </Tooltip>
        );
      })}
    </div>
  );
};
